/**
 * Token Refresh Hook - Implements Sliding Sessions
 * 
 * This hook automatically refreshes the JWT token while the user is active,
 * preventing unexpected logouts during active work (e.g., typing in a long form).
 * 
 * How it works:
 * 1. Sets up an axios interceptor to handle 401 errors
 * 2. Periodically refreshes the token every 15 minutes while user is active
 * 3. Tracks user activity (mouse move, key press, etc.)
 * 4. Only refreshes if user has been active recently
 */

import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getAuthToken, setAuthToken, clearAuthToken } from '../utils/authHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Refresh token every 15 minutes (well before 24hr expiry)
const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes

// Consider user inactive if no activity for 30 minutes
const ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

// Track if interceptor is already setup (avoid duplicates)
let interceptorSetup = false;
let refreshTokenPromise = null; // Prevent concurrent refresh requests

/**
 * Refresh the authentication token.
 *
 * Return contract (Jun 2026 session-stability fix):
 *   - resolves with the new token string on success
 *   - resolves with null ONLY when the session is DEFINITIVELY invalid
 *     (no token in storage, or /auth/refresh answered 401/403)
 *   - THROWS on transient failures (network error, timeout, 5xx) —
 *     callers must NOT log the user out in that case.
 */
export const refreshAuthToken = async () => {
  // If already refreshing, return the existing promise
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  const token = getAuthToken();
  if (!token) {
    return null;
  }

  refreshTokenPromise = (async () => {
    try {
      void 0 && console.log('[TOKEN] Refreshing token...');
      const response = await axios.post(`${API}/auth/refresh`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        // Skip interceptor for refresh requests to avoid infinite loops
        _skipInterceptor: true
      });

      const { access_token } = response.data;
      if (access_token) {
        setAuthToken(access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        void 0 && console.log('[TOKEN] Token refreshed successfully');
        return access_token;
      }
      return null;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // The refresh endpoint itself rejected the token — the session
        // is genuinely invalid (expired/disabled/revoked).
        console.error('[TOKEN] Refresh rejected by server — session invalid');
        return null;
      }
      // Network blip / timeout / 5xx — the session may be perfectly fine.
      console.error('[TOKEN] Transient refresh failure (keeping session):', error.message);
      throw error;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

/**
 * Setup axios interceptor for error normalization.
 *
 * (Jun 2026 session-stability fix) 401 refresh-and-retry now lives in the
 * FIRST-registered global interceptor in App.js — having it here too caused
 * a race where App.js's hard redirect fired before this one could retry.
 * This interceptor now only normalizes Pydantic error bodies.
 */
export const setupTokenRefreshInterceptor = (_logout) => {
  if (interceptorSetup) {
    return;
  }

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Normalize Pydantic V2 validation errors: detail may be an array of
      // {type, loc, msg, input, url} objects.  Convert to a readable string so
      // toast.error() and other React renderers never receive a raw object.
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        error.response.data.detail = error.response.data.detail
          .map((e) => e.msg || JSON.stringify(e))
          .join('; ');
      }

      return Promise.reject(error);
    }
  );

  interceptorSetup = true;
  void 0 && console.log('[TOKEN] Token refresh interceptor setup complete');
};

/**
 * Hook to enable sliding sessions
 * Automatically refreshes tokens while user is active
 */
export const useTokenRefresh = (logout) => {
  const lastActivityRef = useRef(Date.now());
  const refreshIntervalRef = useRef(null);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check if user has been active recently
  const isUserActive = useCallback(() => {
    return Date.now() - lastActivityRef.current < ACTIVITY_TIMEOUT;
  }, []);

  // Refresh token if user is active
  const conditionalRefresh = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      return;
    }

    if (isUserActive()) {
      void 0 && console.log('[TOKEN] User is active, refreshing token...');
      await refreshAuthToken();
    } else {
      void 0 && console.log('[TOKEN] User inactive, skipping token refresh');
    }
  }, [isUserActive]);

  useEffect(() => {
    // Setup axios interceptor
    setupTokenRefreshInterceptor(logout);

    // Setup activity listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    // Throttle activity updates to avoid excessive updates
    let lastUpdate = 0;
    const throttledUpdate = () => {
      const now = Date.now();
      if (now - lastUpdate > 5000) { // Only update every 5 seconds max
        lastUpdate = now;
        updateActivity();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, throttledUpdate, { passive: true });
    });

    // Start periodic token refresh
    refreshIntervalRef.current = setInterval(conditionalRefresh, TOKEN_REFRESH_INTERVAL);

    // Initial activity timestamp
    updateActivity();

    return () => {
      // Cleanup
      events.forEach(event => {
        window.removeEventListener(event, throttledUpdate);
      });
      
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [logout, updateActivity, conditionalRefresh]);

  return {
    updateActivity,
    isUserActive,
    refreshToken: conditionalRefresh
  };
};

export default useTokenRefresh;
